/**
 * Brilho do "Atlas" (spec 011, 03/10/2026 — "semelhante à Siri do iPhone"):
 * luz colorida nas bordas da tela enquanto o Atlas espera o pedido ou faz o
 * que foi pedido, e uma etiqueta embaixo com o que ele entendeu. Some sozinho
 * com ✓ (deu) ou ✕ (não deu) — o bipe continua dizendo o mesmo pra quem não
 * está olhando. Estado em `atlasState.ts`; animação em `index.css` (Atlas).
 *
 * Esperando o pedido (e no "não entendi"), um cartão acima da etiqueta lista
 * os pedidos que valem nesta tela (05/10/2026 — "pra ficar mais fácil de eu
 * lembrar as opções"); some assim que o pedido chega.
 *
 * Por cima de tudo e sem bloquear clique (`pointer-events: none`). Montado
 * no Shell; vai pro `body` pra não ficar preso no empilhamento de ninguém.
 */
import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check, X } from 'lucide-react';
import { DICAS_ATLAS, useAtlasEstado, type AtlasEstado } from '@/services/voice/wake/atlasState';

export const AtlasGlow = () => {
  const estado = useAtlasEstado();
  // Some com a última etiqueta na tela (o fade não pode piscar vazio).
  const ultimo = useRef<AtlasEstado | null>(null);
  if (estado) ultimo.current = estado;
  const mostrado = estado ?? ultimo.current;
  const fase = estado?.fase ?? 'off';
  // A lista também some sem piscar vazia: fica a última até o fade acabar.
  const ultimasDicas = useRef<AtlasEstado['dicas']>(undefined);
  if (estado?.dicas?.length) ultimasDicas.current = estado.dicas;
  const comDicas = !!estado?.dicas?.length;
  const dicas = ultimasDicas.current ?? [];
  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div className="atlas-glow" data-fase={fase} data-testid="atlas-glow" aria-hidden="true">
        <div className="atlas-glow__halo">
          <div className="atlas-glow__anel" />
        </div>
        <div className="atlas-glow__anel" />
      </div>
      <div
        data-visivel={comDicas}
        data-testid="atlas-dicas"
        className="atlas-dicas rounded-2xl border border-outline-variant/40 bg-surface-container-high/95 px-5 py-4 text-on-surface shadow-2xl backdrop-blur"
      >
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
          {mostrado?.fase === 'recusa' ? 'Diga “Atlas” e um destes pedidos' : 'Diga um pedido'}
        </p>
        <ul className="grid grid-cols-[auto_1fr] items-baseline gap-x-4 gap-y-1.5">
          {dicas.map((c) => (
            <li key={c} className="contents">
              <span className="whitespace-nowrap text-base font-bold">“{DICAS_ATLAS[c].fala}”</span>
              <span className="text-sm text-on-surface-variant">{DICAS_ATLAS[c].faz}</span>
            </li>
          ))}
        </ul>
      </div>
      <div
        role="status"
        aria-live="polite"
        data-fase={fase}
        className="atlas-rotulo flex items-center gap-2 rounded-full border border-outline-variant/40 bg-surface-container-high/90 px-4 py-2 text-sm text-on-surface shadow-2xl backdrop-blur"
      >
        {mostrado && (
          <>
            {mostrado.fase === 'feito' ? (
              <Check size={16} className="shrink-0 text-success" />
            ) : mostrado.fase === 'recusa' ? (
              <X size={16} className="shrink-0 text-error" />
            ) : (
              <span className="atlas-rotulo__ponto" />
            )}
            <span className="font-bold">Atlas</span>
            <span aria-hidden="true" className="text-on-surface-variant">·</span>
            <span className={mostrado.fase === 'ouvindo' ? 'text-on-surface-variant' : ''}>{mostrado.rotulo}</span>
          </>
        )}
      </div>
    </>,
    document.body,
  );
};
